import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Eye, EyeOff,
  Loader2, RefreshCw, Search, Utensils,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  APPOINTMENT_STATUS, addDays, daysInMonth, formatDisplayDate, formatShortDate,
  formatWeekdayShort, getViewRange, startOfMonth, startOfWeek, timeToMinutes, toYmd,
} from "@/utils/calendarHelpers";
import {
  CalendarEventCard, DoctorMultiSelect, GRID_DAY_END, GRID_DAY_START, GRID_HOUR_HEIGHT,
  buildGridStyle, filterAppointmentsByDoctors, filterDoctorsBySelection, getGridEventStyle,
  getPatientName, matchesDoctorId,
} from "@/components/calendar/calendarShared";
import "./appointment-calendar.css";

const VIEW_MODES = [
  { id: "day", label: "Gün" },
  { id: "week", label: "Hafta" },
  { id: "month", label: "Ay" },
];

const LUNCH_START = "13:00";
const LUNCH_END = "14:00";

const AppointmentCalendarV2Page = ({ apiBase, isDark, textClass, labelClass, cardClass, inputClass }) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("day");
  const [doctorFilter, setDoctorFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [showEmpty, setShowEmpty] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const viewRange = useMemo(() => getViewRange(viewMode, selectedDate), [viewMode, selectedDate]);
  const hourCount = GRID_DAY_END - GRID_DAY_START;
  const hours = useMemo(() => Array.from({ length: hourCount }, (_, i) => GRID_DAY_START + i), [hourCount]);
  const dayYmd = toYmd(selectedDate);

  const filteredAppointments = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byDoctor = filterAppointmentsByDoctors(appointments, doctorFilter);
    return byDoctor.filter((a) => {
      if (viewMode === "day" && a.date !== dayYmd) return false;
      if (!term) return true;
      const patient = getPatientName(a).toLowerCase();
      return patient.includes(term)
        || (a.note || "").toLowerCase().includes(term)
        || (a.typeName || "").toLowerCase().includes(term);
    });
  }, [appointments, search, doctorFilter, viewMode, dayYmd]);

  const visibleDoctors = useMemo(
    () => filterDoctorsBySelection(doctors, doctorFilter),
    [doctors, doctorFilter]
  );

  const displayDoctors = useMemo(
    () => (visibleDoctors.length ? visibleDoctors : doctors),
    [visibleDoctors, doctors]
  );

  const dayAppointments = useMemo(
    () => filteredAppointments.filter((a) => a.date === dayYmd),
    [filteredAppointments, dayYmd]
  );

  const statusCounts = useMemo(() => {
    const source = viewMode === "day" ? dayAppointments : filteredAppointments;
    const counts = {};
    source.forEach((a) => { counts[a.status] = (counts[a.status] || 0) + 1; });
    return counts;
  }, [dayAppointments, filteredAppointments, viewMode]);

  const appointmentCountByDoctor = useMemo(() => {
    const map = {};
    visibleDoctors.forEach((d) => { map[d.id] = 0; });
    dayAppointments.forEach((a) => { map[a.doctorId] = (map[a.doctorId] || 0) + 1; });
    return map;
  }, [visibleDoctors, dayAppointments]);

  const getEventStyle = (start, end) => getGridEventStyle(start, end, GRID_DAY_START, GRID_HOUR_HEIGHT);

  const loadData = useCallback(async () => {
    if (!apiBase) return;
    setLoading(true);
    try {
      const [calRes, docRes] = await Promise.all([
        axios.get(`${apiBase}/calendar`, {
          params: {
            start_date: viewRange.startYmd,
            end_date: viewRange.endYmd,
            search,
            auto_sync: viewMode === "day" ? "1" : undefined,
          },
        }),
        axios.get(`${apiBase}/calendar`, { params: { doctors: 1 } }),
      ]);
      setAppointments(calRes.data?.items || []);
      setDoctors(docRes.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Takvim verisi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase, viewRange.startYmd, viewRange.endYmd, search, viewMode]);

  const runSync = async () => {
    if (!apiBase) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${apiBase}/calendar?sync=1`, { sync: true, trigger: "manual" });
      toast.success(res.data?.message || "Senkron tamamlandı");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senkron başarısız");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { loadData(); }, [loadData]);

  const navStep = viewMode === "week" ? 7 : viewMode === "month" ? 30 : 1;

  const renderDayGrid = (date, appts, cols) => {
    const ymd = toYmd(date);
    const dayItems = appts.filter((a) => a.date === ymd);
    const gridCols = cols.length ? cols : doctors.slice(0, 1);
    const localGridStyle = buildGridStyle(gridCols.length, hourCount, GRID_HOUR_HEIGHT);
    const showLunch = viewMode === "day" && timeToMinutes(LUNCH_START) >= GRID_DAY_START * 60 && timeToMinutes(LUNCH_START) < GRID_DAY_END * 60;

    return (
      <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
        {viewMode !== "day" && (
          <div className={`px-4 py-2.5 border-b text-sm font-semibold ${textClass}`}>
            {formatWeekdayShort(date)} · {formatShortDate(date)}
          </div>
        )}
        <div className="appt-cal-v2-grid border-b border-[var(--appt-grid-line)]" style={localGridStyle}>
          <div className={`p-4 text-[11px] font-semibold uppercase tracking-wider ${labelClass}`}>Saat</div>
          {gridCols.map((doc) => (
            <div key={doc.id} className={`p-4 border-l border-[var(--appt-grid-line)] appt-cal-v2-doc-head ${isDark ? "bg-slate-900/30" : "bg-gray-50/90"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${doc.tone} text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm`}>
                  {doc.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate leading-tight ${textClass}`}>{doc.name}</p>
                  <p className={`text-xs truncate mt-0.5 ${labelClass}`}>{doc.specialty || "Doktor"}</p>
                </div>
                <span className={`text-xs tabular-nums px-2 py-0.5 rounded-md shrink-0 ${isDark ? "bg-slate-800 text-slate-300" : "bg-white text-gray-600 border border-gray-200 shadow-sm"}`}>
                  {dayItems.filter((a) => matchesDoctorId(a.doctorId, doc.id)).length}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] appt-cal-v2-scroll">
          <div className="appt-cal-v2-grid relative" style={localGridStyle}>
            <div className="appt-cal-time-col">
              {hours.map((h) => (
                <div key={h} className={`appt-cal-v2-hour-label ${labelClass}`} style={{ height: GRID_HOUR_HEIGHT }}>
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>
            {gridCols.map((doc) => (
              <div key={doc.id} className="appt-cal-v2-doctor-col">
                {dayItems.filter((a) => matchesDoctorId(a.doctorId, doc.id)).map((appt) => (
                  <CalendarEventCard key={appt.id} item={appt} isDark={isDark} style={getEventStyle(appt.start, appt.end)} />
                ))}
                {!showEmpty && dayItems.filter((a) => matchesDoctorId(a.doctorId, doc.id)).length === 0 && (
                  <div className={`absolute inset-0 flex items-center justify-center text-xs ${labelClass} opacity-30`}>Boş</div>
                )}
              </div>
            ))}
          </div>

          {showLunch && (
            <div className="appt-cal-v2-lunch-row" style={localGridStyle}>
              <div className={`flex items-center justify-end pr-3 text-xs font-medium ${labelClass}`} style={{ height: 44 }}>
                {LUNCH_START}
              </div>
              {gridCols.map((doc) => (
                <div
                  key={`lunch-${doc.id}`}
                  className={`flex items-center justify-center gap-2 text-xs font-medium border-l border-[var(--appt-grid-line)] appt-cal-block-striped ${isDark ? "text-slate-400 bg-slate-800/20" : "text-slate-500 bg-slate-50"}`}
                  style={{ height: 44 }}
                >
                  <Utensils className="w-3.5 h-3.5" />
                  Beslenme
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const start = startOfWeek(selectedDate);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return (
      <div className="grid gap-4 xl:grid-cols-2">
        {days.map((d) => renderDayGrid(d, filteredAppointments, displayDoctors))}
      </div>
    );
  };

  const renderMonthView = () => {
    const first = startOfMonth(selectedDate);
    const total = daysInMonth(selectedDate);
    const startPad = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), d));

    return (
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
            <div key={d} className={`text-center text-xs font-semibold py-1 ${labelClass}`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`empty-${idx}`} className="min-h-[110px]" />;
            const ymd = toYmd(cell);
            const items = filteredAppointments.filter((a) => a.date === ymd);
            const isToday = ymd === toYmd(new Date());
            const isSelected = ymd === dayYmd;
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => { setSelectedDate(cell); setViewMode("day"); }}
                className={`min-h-[110px] rounded-xl border p-2.5 text-left transition-all hover:shadow-md ${
                  isDark ? "border-slate-800 hover:bg-slate-800/40" : "border-gray-100 hover:bg-gray-50"
                } ${isToday ? "ring-2 ring-blue-500/40" : ""} ${isSelected ? isDark ? "bg-blue-500/10 border-blue-500/40" : "bg-blue-50 border-blue-200" : ""}`}
              >
                <div className={`text-sm font-bold mb-2 ${textClass}`}>{cell.getDate()}</div>
                <div className="space-y-1">
                  {items.slice(0, 4).map((a) => {
                    const st = APPOINTMENT_STATUS[a.status] || APPOINTMENT_STATUS.other;
                    return (
                      <div key={a.id} className={`text-[10px] leading-tight truncate ${labelClass}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${st.dot}`} />
                        <span className="font-medium">{a.start}</span> {getPatientName(a)}
                      </div>
                    );
                  })}
                  {items.length > 4 && <div className={`text-[10px] ${labelClass}`}>+{items.length - 4} randevu</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`appt-cal-root appt-cal-v2-root space-y-4 ${isDark ? "dark" : ""}`}>
      {/* Üst başlık — mockup */}
      <div className={`rounded-xl border p-5 md:p-6 ${cardClass}`}>
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-bold tracking-tight ${textClass}`}>Randevu Takvimi</h2>
            <p className={`text-sm mt-1 ${labelClass}`}>
              {visibleDoctors.length || doctors.length} doktorun programını tek bakışta görüntüleyin ve yönetin.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className={`inline-flex items-center rounded-lg border p-0.5 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"}`}>
              <Button type="button" size="icon" variant="ghost" className="h-9 w-9" onClick={() => setSelectedDate((d) => addDays(d, -navStep))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-9 px-4 text-sm font-medium" onClick={() => setSelectedDate(new Date())}>
                Bugün
              </Button>
              <Button type="button" size="icon" variant="ghost" className="h-9 w-9" onClick={() => setSelectedDate((d) => addDays(d, navStep))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${isDark ? "border-slate-700 bg-slate-800/50 text-slate-100" : "border-gray-200 bg-white text-gray-800 shadow-sm"}`}>
              <CalendarIcon className="w-4 h-4 opacity-50" />
              {formatDisplayDate(selectedDate)}
            </div>
            <div className={`inline-flex rounded-lg p-1 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setViewMode(mode.id)}
                  className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
                    viewMode === mode.id
                      ? "bg-blue-600 text-white shadow-sm"
                      : isDark ? "text-slate-400 hover:text-white" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className={`h-9 ${inputClass}`} disabled={syncing} onClick={runSync}>
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Senkron
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`rounded-xl border p-4 flex flex-col lg:flex-row lg:items-center gap-3 ${cardClass}`}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelClass}`} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hasta adıyla ara..." className={`pl-10 h-10 ${inputClass}`} />
        </div>
        <DoctorMultiSelect
          doctors={doctors}
          values={doctorFilter}
          onChange={setDoctorFilter}
          isDark={isDark}
          inputClass={inputClass}
          labelClass={labelClass}
          className="h-10 w-full lg:w-52"
        />
        <Button type="button" variant="outline" size="sm" className={`h-10 shrink-0 ${inputClass}`} onClick={() => setShowEmpty((v) => !v)}>
          {showEmpty ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
          Boş saatleri {showEmpty ? "göster" : "gizle"}
        </Button>
        <div className={`flex flex-wrap items-center gap-4 text-xs ${labelClass}`}>
          {Object.entries(APPOINTMENT_STATUS).map(([key, meta]) => (
            <span key={key} className="inline-flex items-center gap-1.5" title={meta.label}>
              <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
              {statusCounts[key] ?? 0}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={`rounded-xl border p-16 text-center ${cardClass}`}>
          <Loader2 className={`w-10 h-10 mx-auto animate-spin ${labelClass}`} />
          <p className={`text-sm mt-4 ${labelClass}`}>Randevular yükleniyor...</p>
        </div>
      ) : doctors.length === 0 ? (
        <div className={`rounded-xl border p-12 text-center ${cardClass}`}>
          <CalendarIcon className={`w-12 h-12 mx-auto mb-4 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
          <p className={`text-base font-medium ${textClass}`}>Henüz randevu verisi yok</p>
          <p className={`text-sm mt-2 ${labelClass}`}>Senkron yaparak MedicaSimple verilerini çekin.</p>
        </div>
      ) : viewMode === "day" ? (
        renderDayGrid(selectedDate, filteredAppointments, displayDoctors)
      ) : viewMode === "week" ? (
        renderWeekView()
      ) : (
        renderMonthView()
      )}
    </div>
  );
};

export default AppointmentCalendarV2Page;
